<template>
  <div class="card">
    <div
      class="card-body pt-3"
      :class="{
        'pb-3': options && options.acceptedFiles,
      }"
    >
      <div class="mb-1">
        <small v-if="options && options.maxFiles" class="text-muted">
          You can upload up to {{ options.maxFiles }} files.
        </small>
        <small class="text-muted">
          Maximum file size: {{ maxFilesizeLabel }}
        </small>
      </div>
      <div class="dropzone dropzone-multiple" ref="dropzone">
        <!-- Fallback -->
        <div class="fallback">
          <div class="custom-file">
            <input
              type="file"
              class="custom-file-input"
              id="customFileUploadMultiple"
              multiple
            />
            <label class="custom-file-label" for="customFileUploadMultiple"
              >Failed uploads</label
            >
          </div>
        </div>

        <!-- Preview -->
        <ul
          class="
            dz-preview dz-preview-multiple
            list-group list-group-lg list-group-flush
          "
        >
          <li class="list-group-item">
            <div class="row align-items-center">
              <div class="col">
                <!-- Heading -->
                <h4 class="mb-1" data-dz-name>...</h4>

                <!-- Text -->
                <small class="text-muted" data-dz-size>...</small>

                <div class="progress progress-sm">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    data-dz-uploadprogress
                    style="width: 0%"
                    aria-valuenow="10"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            </div>
          </li>
        </ul>
        <span v-if="status" class="pt-3 m-0 px-0 text-muted"
          >{{ status }}
          <icon
            v-if="status == 'Processing...'"
            type="circle-notch"
            class="fa-spin"
        /></span>
      </div>
      <div v-if="options && options.acceptedFiles" class="mt-1">
        <small class="text-muted">
          Allowed file types: {{ options.acceptedFiles }}
        </small>
      </div>
      <div v-if="showFailedFiles" class="mt-3">
        <h5>Failed Uploaded Files</h5>
        <ul class="mb-0">
          <li v-for="file in failedFiles" :key="file.name">{{ file.name }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import Dropzone from "dropzone";
import Swal from "sweetalert2";
import { mapState } from "vuex";
import { formatFileSize } from "@/utils/fileHelpers.js";
export default {
  computed: {
    ...mapState({
      baseUrl: (state) => state.appConfig.api,
    }),
    maxFilesizeLabel() {
      const mb = this.options?.maxFilesize || 500;

      return formatFileSize(mb * 1024 * 1024);
    },
  },
  data() {
    return {
      status: null,
      dropzoneComponent: null,
      showFailedFiles: false,
      failedFiles: [],
      uploadQueue: [],
      isProcessingQueue: false,
    };
  },
  props: {
    options: {
      type: Object,
      required: true,
    },
    uploadHandler: {
      type: Function,
      default: null,
    },
  },
  watch: {
    options: function () {
      this.dropzoneComponent.URL = this.baseUrl + this.options.url;
      this.dropzoneComponent.options.url = this.baseUrl + this.options.url;
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init: function () {
      let vm = this;
      let uploadedFiles = 0;
      let failFiles = 0;
      let totalFiles = 0;
      Dropzone.autoDiscover = false;
      Dropzone.thumbnailWidth = null;
      Dropzone.thumbnailHeight = null;
      let el = this.$refs.dropzone;

      // Dropzone's own maxFilesize check always reports MiB; disabled below in favor of our own.
      const configuredMaxFilesizeMB = this.options?.maxFilesize || 500;

      // Base options
      let options = {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("ARSS_TOKEN"),
        },
        ...this.options,
        maxFilesize: null,
        url: this.uploadHandler
          ? "/placeholder"
          : this.baseUrl + this.options.url,
        previewsContainer: el.querySelector(".dz-preview"),
        previewTemplate: el.querySelector(".dz-preview").innerHTML,
        parallelUploads: 1,
        autoProcessQueue: !this.uploadHandler,
        accept: function (file, done) {
          const maxBytes = configuredMaxFilesizeMB * 1024 * 1024;
          if (file.size > maxBytes) {
            done(
              `File is too big (${formatFileSize(
                file.size
              )}). Max filesize: ${formatFileSize(maxBytes)}.`
            );
            return;
          }

          done();

          if (vm.uploadHandler) {
            vm.uploadQueue.push(file);
            vm.processUploadQueue();
          }
        },
        init: function () {
          // Standard Dropzone events (only used when uploadHandler is NOT provided)
          if (!vm.uploadHandler) {
            this.on("success", function (file) {
              if (file.status == "success") {
                uploadedFiles++;
                if (file.status == "success") {
                  vm.$emit("success", file);
                  vm.deleteFile(file);
                }

                if (uploadedFiles + failFiles === totalFiles) {
                  if (!this.options?.noFinalAlert) {
                    Swal.fire(
                      "Upload Complete",
                      `${uploadedFiles} ${
                        uploadedFiles === 1 ? "file" : "files"
                      } uploaded successfully.${
                        failFiles
                          ? `<br>${failFiles} file${
                              failFiles === 1 ? "" : "s"
                            } failed to upload.`
                          : ""
                      }`,
                      "success"
                    );
                  }
                  vm.status = null;
                  uploadedFiles = 0;
                  failFiles = 0;
                  totalFiles = 0;
                }
              }
            });
            this.on("uploadprogress", function (_, progress) {
              if (progress == 100) {
                vm.status = "Processing...";
              }
            });
            this.on("error", function (file, errorMessage) {
              vm.$emit("error", file);
              vm.status = null;
              vm.showFailedFiles = true;
              vm.failedFiles.push({
                name: file.name,
              });
              failFiles++;
              vm.deleteFile(file);
              Swal.fire(
                "Upload Error",
                errorMessage?.error?.message || errorMessage,
                "error"
              );
              if (
                uploadedFiles + failFiles === totalFiles &&
                totalFiles !== failFiles
              ) {
                if (!this.options?.noFinalAlert) {
                  Swal.fire(
                    "Upload Complete",
                    `${uploadedFiles} ${
                      uploadedFiles === 1 ? "file" : "files"
                    } uploaded successfully.${
                      failFiles
                        ? `${failFiles} file${
                            failFiles === 1 ? "" : "s"
                          } failed to upload`
                        : ""
                    }`,
                    "success"
                  );
                }
                uploadedFiles = 0;
                failFiles = 0;
                totalFiles = 0;
              }
              if (uploadedFiles + failFiles === totalFiles) {
                uploadedFiles = 0;
                failFiles = 0;
                totalFiles = 0;
              }
            });
            this.on("sending", function () {
              vm.status = "Uploading...";
            });
          }

          if (vm.uploadHandler) {
            this.on("error", function (file, errorMessage) {
              vm.$emit("error", file);
              vm.status = null;
              vm.showFailedFiles = true;
              vm.failedFiles.push({ name: file.name });
              vm.deleteFile(file);
              Swal.fire(
                "Upload Error",
                errorMessage?.error?.message || errorMessage,
                "error"
              );
            });
          }

          // Common events for both modes
          this.on("maxfilesexceeded", function () {
            Swal.fire(
              "Exceeded maximum upload limit",
              `You can upload a maximum of ${this.options.maxFiles} files`,
              "error"
            );
          });

          this.on("addedfile", function () {
            if (totalFiles === 0) {
              vm.failedFiles = [];
              vm.showFailedFiles = false;
            }
            totalFiles++;
          });
        },
      };
      el.querySelector(".dz-preview").innerHTML = "";
      this.dropzoneComponent = new Dropzone(el, options);
    },
    /**
     * Process the upload queue sequentially using the custom uploadHandler
     */
    async processUploadQueue() {
      if (this.isProcessingQueue || this.uploadQueue.length === 0) {
        return;
      }

      this.isProcessingQueue = true;
      let uploadedFiles = 0;
      let failFiles = 0;
      const totalFiles = this.uploadQueue.length;

      while (this.uploadQueue.length > 0) {
        const file = this.uploadQueue.shift();
        this.status = "Uploading...";

        try {
          await this.uploadHandler(file, (progress) => {
            // Update progress bar
            const progressBar = file.previewElement?.querySelector(
              "[data-dz-uploadprogress]"
            );
            if (progressBar) {
              progressBar.style.width = `${progress}%`;
            }
            if (progress === 100) {
              this.status = "Processing...";
            }
          });

          // Success
          file.status = "success";
          uploadedFiles++;
          this.$emit("success", file);
          this.deleteFile(file);
        } catch (error) {
          // Error
          failFiles++;
          this.$emit("error", file);
          this.showFailedFiles = true;
          this.failedFiles.push({ name: file.name });
          this.deleteFile(file);

          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "Upload failed";
          Swal.fire("Upload Error", errorMessage, "error");
        }
      }

      // All files processed - show completion alert
      if (totalFiles > 0 && !this.options?.noFinalAlert) {
        Swal.fire(
          "Upload Complete",
          `${uploadedFiles} ${
            uploadedFiles === 1 ? "file" : "files"
          } uploaded successfully.${
            failFiles
              ? `<br>${failFiles} file${
                  failFiles === 1 ? "" : "s"
                } failed to upload.`
              : ""
          }`,
          "success"
        );
      }

      this.status = null;
      this.isProcessingQueue = false;
    },
    /**
     * Delete a file from the dropzone manually by manipulating the DOM.
     * @param file
     *
     * @remarks
     * I am using this function instead of this.removeFile(file) because
     * this.removeFile(file) is deleting the file from the library and the
     * dom so the maxFiles limit is not working as expected so I am deleting
     * the file from the dom only and not from the library so the maxFiles
     * limit will work as expected
     */
    deleteFile: function (file) {
      if (file.previewElement) {
        file.previewElement.remove();
      }
    },
    /**
     * Remove a file from the dropzone by file uuid.
     * @param file
     *
     * @remarks
     * This function is used from the parent component to remove a file from the dropzone
     * by file uuid.
     */
    removeFileByUuid: function (uuid) {
      const file = this.dropzoneComponent.files.find(
        (f) => f.upload.uuid === uuid
      );
      if (file) {
        this.dropzoneComponent.removeFile(file);
      }
    },
  },
};
</script>
